import { setLocale } from '@vee-validate/i18n'
import { defineStore } from 'pinia'
import { usePrimeVue } from 'primevue/config'
import { type Ref, ref } from 'vue'

import { PredefinedText } from '@/enums/common.ts'
import { isAuthenticated } from '@/libs/auth'
import dayjs from '@/libs/dayjs'
import { parseError } from '@/libs/error.ts'
import i18n, { type i18nType } from '@/libs/i18n'
import { changePrimevueLanguage } from '@/libs/primevue/primevue.ts'
import { PrintError } from '@/libs/toast.ts'
import { useFetchStore } from '@/stores/fetch.ts'
import type { User } from '@/types/User.ts'

export const useAppStore = defineStore('app', () => {
    const primevue = usePrimeVue()

    const JWT: Ref<string> = ref(localStorage.getItem('JWT') || '')
    const windowWidth: Ref<number> = ref(0)
    const loaderActive: Ref<number> = ref(0)
    const loggedIn: Ref<boolean> = ref(isAuthenticated())
    const userData: Ref<User> = ref({
        id: '',
        name: '',
        username: '',
        image: '',
        roles: []
    })

    const changeLocale = (newLocale: string) => {
        const { locale: i18nLocale } = i18n.global
        i18nLocale.value = newLocale as i18nType
        dayjs.locale(newLocale)
        setLocale(newLocale)
        changePrimevueLanguage(primevue, newLocale)
        localStorage.setItem('locale', newLocale)
    }

    const resizeHandler = (): void => {
        windowWidth.value = window.innerWidth
    }

    const isMobile = (): boolean => {
        return windowWidth.value < 1024
    }

    const startLoading = () => {
        loaderActive.value++
    }
    const stopLoading = () => {
        if (loaderActive.value > 0) loaderActive.value--
    }

    const setJWTCookie = () => {
        if (JWT.value.length > 0) document.cookie = `JWT=${JWT.value};path=/;`
        else document.cookie = 'JWT=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
    }

    const setUserData = (data: User) => {
        userData.value = data
    }

    // getters
    const isLoaderActive = () => {
        return !!loaderActive.value
    }

    const getUserData = () => {
        return userData.value
    }

    const isLoggedIn = () => {
        return loggedIn.value
    }

    const getJWT = () => {
        return JWT.value
    }

    const loadUserData = async() => {
        if (!isLoggedIn()) return

        const { response, data } = await useFetchStore().fetch('/user_data').get().json<User>()
        if (response.value && response.value.status === 200 && data.value) {
            setUserData(data.value)
        } else {
            PrintError(parseError(data.value, PredefinedText.value.ErrorLoadData))
        }
    }

    const setLogin = async(data: User, jwt: string) => {
        localStorage.setItem('JWT', jwt)
        userData.value = data
        JWT.value = jwt
        setJWTCookie()
        await loadUserData()
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        userData.value = {
            id: '',
            name: '',
            username: '',
            image: '',
            roles: []
        }
        JWT.value = ''
        setJWTCookie()
    }

    return {
        windowWidth,
        changeLocale,
        isMobile,
        resizeHandler,
        setLogin,
        setLogout,
        isLoaderActive,
        startLoading,
        stopLoading,
        setUserData,
        setJWTCookie,
        getUserData,
        isLoggedIn,
        getJWT,
        loadUserData
    }
})
